import { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";

import { Form } from '../form/FormComponents';
import SelectMenuItem from "./SelectMenuItem";
import resolveDisplay from "./resolveDisplay";
import { nextSelectable } from "./SelectModal";
import { evaluateCondition } from "../../../../common/model/condition/evaluate";
import useTranslation from "../../hooks/useTranslation";

const Container = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`

const InputField = styled(Form.Control)`
    height: 100%;
`

// Wider than a narrow grid cell: an account reads as "number - name".
// `dropdownAlign: 'right'` opens it leftwards, for a field at the right edge.
const SearchResults = styled.div`
    position: absolute;
    top: 100%;
    ${({ $alignRight }) => $alignRight ? 'right: 0;' : 'left: 0;'}
    min-width: max(100%, 20rem);
    max-height: 18rem;
    overflow-y: auto;
    box-shadow: 0 2px 5px rgb(0 0 0 / 28%);
    border-radius: 0.375rem;
    z-index: 1000;
`

const ListGroup = styled.ul`
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.dropdown.background};
    padding-left: 0;
    margin: 0;
    list-style: none;
`

/*
 * Connect without the modal (`inline`): the field is the search box and the
 * matches drop down beneath it, so a picker can be reached with Tab and filled
 * by typing — the grid's account columns. The data is Connect's own: below
 * three characters the loaded page is filtered here, from three on Connect
 * searches the server (onSearch), so the whole chart is reachable.
 *
 * While typing, the first row that can be picked is highlighted, so Enter or
 * Tab takes it. An emptied field picks nothing — whoever holds it decides
 * what an empty value means.
 */
const ConnectInlineControl = ({
    data,
    stale = false,
    valueName,
    onSelect,
    onSearch,
    placeholder,
    options = {},
    menuItemField = 'name',
    listDisplay,
    isRed,
    autoFocus,
    testId,
    inputId,
    name,
}) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState(valueName || '');
    const [debounced, setDebounced] = useState('');
    // Only typed text filters: opening the field shows the list as it is.
    const [typed, setTyped] = useState(false);
    const [isVisible, setVisible] = useState(false);
    const [cursor, setCursor] = useState(-1);
    const listRef = useRef(null);

    useEffect(() => { setSearch(valueName || ''); setTyped(false); }, [valueName]);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(typed ? search : ''), 150);
        return () => clearTimeout(timer);
    }, [search, typed]);

    useEffect(() => { onSearch?.(debounced); }, [debounced, onSearch]);

    const highlightCondition = options.highlight?.condition;
    const isEmphasized = (item) =>
        !!highlightCondition && !item?.header && evaluateCondition(highlightCondition, { values: item });
    const selectableCondition = options.selectable?.condition;
    const isDisabled = (item) =>
        !item?.header && !item?.unselectable && !evaluateCondition(selectableCondition, { values: item });
    const canPick = (item) => !!item && !item.header && !item.unselectable && !isDisabled(item);

    // Same split as SelectModal: three characters and up come back already
    // searched by the server, on the entity's own searchFields — but only
    // once the answer is for what is typed now. Until then (`stale`, or the
    // term still settling) the list in hand is filtered here, so Enter can
    // never take a row that does not match the text.
    const term = typed ? search : '';
    const suggestions = useMemo(() => {
        const list = data?.list || [];
        if (!term) return list;
        if (term.length >= 3 && debounced === term && !stale) return list;
        const lower = term.toLowerCase();
        return list.filter(item => {
            if (item.header) return false;
            if (item.name?.toLowerCase().includes(lower)) return true;
            if (item.id?.toLowerCase().includes(lower)) return true;
            if (listDisplay?.params) return listDisplay.params.some(f => String(item[f] ?? '').toLowerCase().includes(lower));
            return false;
        });
    }, [data?.list, term, debounced, stale, listDisplay]);

    // Typing highlights the first match, so Enter takes it at once.
    useEffect(() => {
        setCursor(typed && search ? nextSelectable(suggestions, -1, 1, canPick) : -1);
        listRef.current?.parentElement?.scrollTo({ top: 0 });
    }, [suggestions]);

    useEffect(() => {
        if (cursor < 0 || !listRef.current) return;
        listRef.current.children[cursor]?.scrollIntoView({ block: 'nearest' });
    }, [cursor]);

    const pick = (item) => {
        setSearch(resolveDisplay(item, menuItemField, listDisplay));
        setTyped(false);
        setVisible(false);
        onSelect(item);
    };

    // What Enter or Tab takes: the highlighted row — typed for, or arrowed to
    // in a field still empty — or, while searching, the only one left.
    const chosen = () => {
        if (isVisible && cursor >= 0 && canPick(suggestions[cursor])) return suggestions[cursor];
        if (!typed || !search) return null;
        const pickable = suggestions.filter(canPick);
        return pickable.length === 1 ? pickable[0] : null;
    };

    const handleKeyDown = (e) => {
        // Ctrl+arrows belong to whatever holds the field (a grid moves rows).
        if (e.ctrlKey) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!isVisible) setVisible(true);
            else setCursor(c => nextSelectable(suggestions, c, 1, canPick));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCursor(c => nextSelectable(suggestions, c, -1, canPick));
        } else if (e.key === 'Escape') {
            setVisible(false);
            setSearch(valueName || '');
            setTyped(false);
        } else if (e.key === 'Enter' || (e.key === 'Tab' && (typed || cursor >= 0))) {
            const item = chosen();
            if (item) {
                // Picking moves on by itself (the host's setValue); the key
                // must not move on a second time.
                e.preventDefault();
                e.stopPropagation();
                pick(item);
            } else if (e.key === 'Enter') {
                e.preventDefault();
            }
        }
    };

    const handleBlur = () => {
        setVisible(false);
        // Left mid-search: back to the value it holds. Emptied stays empty.
        if (typed && search) setSearch(valueName || '');
        setTyped(false);
    };

    return (
        <Container>
            <InputField
                type="text"
                id={inputId || name}
                name={name}
                autoComplete="off"
                value={search}
                placeholder={t(placeholder)}
                onChange={e => { setSearch(e.target.value); setTyped(true); setVisible(true); }}
                onFocus={e => { e.target.select(); setVisible(true); }}
                onClick={() => setVisible(true)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                autoFocus={autoFocus}
                role="combobox"
                aria-expanded={isVisible}
                aria-autocomplete="list"
                data-testid={testId}
                {...(isRed && { required: true })}
            />
            {isVisible && suggestions.length > 0 && (
                <SearchResults $alignRight={options.dropdownAlign === 'right'}>
                    <ListGroup ref={listRef} role="listbox">
                        {suggestions.map((item, idx) => (
                            <SelectMenuItem
                                {...item}
                                key={item.id ?? idx}
                                name={resolveDisplay(item, menuItemField, listDisplay)}
                                idx={idx}
                                unselectable={item?.unselectable || false}
                                disabled={isDisabled(item)}
                                emphasized={isEmphasized(item)}
                                isHighlighted={cursor === idx}
                                onSelectItem={() => { if (canPick(item)) pick(item); }}
                                testId={testId}
                            />
                        ))}
                    </ListGroup>
                </SearchResults>
            )}
        </Container>
    );
};

export default ConnectInlineControl;
